import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  isSameDay,
} from "date-fns";
import { useMediaQuery, useTheme } from "@mui/material";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

// Shared components
import DataTable from "components/DataTable";
import PageHeader from "components/PageHeader";
import MemberLedgerBook from "components/MemberLedgerBook";

// Custom hooks
import useWelfareData from "layouts/tables/data/welfareReportTableData";
import useMemberLedgerData from "layouts/tables/data/memberLedgerData";

const LEDGER_YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

function Welfare() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [paymentType, setPaymentType] = React.useState("welfare");
  const [viewMode, setViewMode] = React.useState("transactions");

  const {
    columns: allColumns,
    rows,
    isLoading,
    error,
    fetchWelfareData,
    handleExport,
    handleTypeChange,
    selectedMember,
    showMemberDetails,
    handleMemberClick,
    closeMemberDetails,
    churchId,
    MemberDetailsModal,
  } = useWelfareData();

  const {
    rows: ledgerRows,
    meta: ledgerMeta,
    year: ledgerYear,
    isLoading: ledgerLoading,
    error: ledgerError,
    handleYearChange,
    handlePageChange: handleLedgerPageChange,
    search: ledgerSearch,
    handleSearchChange: handleLedgerSearchChange,
  } = useMemberLedgerData("welfare");

  // Define which columns to show/hide based on screen size
  const columns = React.useMemo(() => {
    if (isMobile) {
      return allColumns.filter(
        (col) =>
          col.accessor === "serial" || col.accessor === "memberName" || col.accessor === "amount"
      );
    }
    return allColumns;
  }, [allColumns, isMobile]);

  const [dateRange, setDateRange] = React.useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });

  const [dateRangeType, setDateRangeType] = React.useState("daily");

  const handleStartDateChange = (date) => {
    const newDateRange = {
      startDate: startOfDay(date),
      endDate: dateRange.endDate,
    };
    setDateRange(newDateRange);
    fetchWelfareData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = {
      startDate: dateRange.startDate,
      endDate: endOfDay(date),
    };
    setDateRange(newDateRange);
    fetchWelfareData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const newDateRange = {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    };
    setDateRange(newDateRange);
    setDateRangeType("daily");
    fetchWelfareData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleDateRangeTypeChange = (event) => {
    const type = event.target.value;
    setDateRangeType(type);

    const today = new Date();
    let startDate, endDate;

    switch (type) {
      case "daily":
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case "weekly":
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case "monthly":
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case "yearly":
        startDate = startOfYear(today);
        endDate = endOfYear(today);
        break;
      case "custom":
        // Keep current dates for custom range
        return;
      default:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
    }

    const newDateRange = { startDate, endDate };
    setDateRange(newDateRange);
    fetchWelfareData(startDate, endDate, paymentType);
  };

  const handlePaymentTypeChange = (event) => {
    const type = event.target.value;
    setPaymentType(type);
    handleTypeChange(type);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading Welfare...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box pt={6} pb={3}>
      <PageHeader
        title="Welfare Report"
        subtitle={
          <>
            {formatDisplayDate(dateRange.startDate)}
            {!isSameDay(dateRange.startDate, dateRange.endDate) &&
              ` to ${formatDisplayDate(dateRange.endDate)}`}
          </>
        }
        actions={
          viewMode === "transactions" ? (
            <>
              <TextField
                select
                size="small"
                value={dateRangeType}
                onChange={handleDateRangeTypeChange}
                sx={{ width: 120 }}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </TextField>

              {dateRangeType === "custom" && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="From Date"
                    value={dateRange.startDate}
                    onChange={handleStartDateChange}
                    maxDate={dateRange.endDate}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 150 }} />
                    )}
                  />
                  <DatePicker
                    label="To Date"
                    value={dateRange.endDate}
                    onChange={handleEndDateChange}
                    minDate={dateRange.startDate}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 150 }} />
                    )}
                  />
                </LocalizationProvider>
              )}

              <TextField
                select
                size="small"
                value={paymentType}
                onChange={handlePaymentTypeChange}
                sx={{ width: 120 }}
              >
                <MenuItem value="welfare">Welfare</MenuItem>
                <MenuItem value="tithe">Tithe</MenuItem>
                <MenuItem value="offering">Offering</MenuItem>
                <MenuItem value="donation">Donation</MenuItem>
              </TextField>

              <Button variant="outlined" onClick={handleTodayClick} sx={{ minWidth: "100px" }}>
                Today
              </Button>

              <Button
                variant="outlined"
                onClick={() => handleExport("csv")}
                startIcon={<Icon>download</Icon>}
              >
                CSV
              </Button>

              <Button
                variant="outlined"
                onClick={() => handleExport("pdf")}
                startIcon={<Icon>picture_as_pdf</Icon>}
              >
                PDF
              </Button>

              <Button
                variant="outlined"
                onClick={() => handleExport("print")}
                startIcon={<Icon>print</Icon>}
              >
                Print
              </Button>
            </>
          ) : (
            <TextField
              select
              size="small"
              label="Year"
              value={ledgerYear}
              onChange={(e) => handleYearChange(Number(e.target.value))}
              sx={{ width: 120 }}
            >
              {LEDGER_YEARS.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          )
        }
      />

      <Tabs
        value={viewMode}
        onChange={(_, value) => setViewMode(value)}
        sx={{ mb: 2 }}
        data-print-hide="true"
      >
        <Tab value="transactions" label="Transactions" />
        <Tab value="book" label="Member Book" />
      </Tabs>

      {viewMode === "book" ? (
        <Card sx={{ p: 3 }}>
          <MemberLedgerBook
            rows={ledgerRows}
            meta={ledgerMeta}
            isLoading={ledgerLoading}
            error={ledgerError}
            pagination={ledgerMeta?.pagination}
            onPageChange={handleLedgerPageChange}
            search={ledgerSearch}
            onSearchChange={handleLedgerSearchChange}
            onMemberClick={(row) =>
              handleMemberClick({ member_id: row.member_id, memberName: row.name })
            }
          />
        </Card>
      ) : (
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Box>
                <DataTable
                  table={{
                    columns,
                    rows: rows.map((row) => ({
                      ...row,
                      // Ensure all data is included even if columns are hidden
                      originalData: row.originalData || {
                        id: row.id,
                        memberName: row.memberName,
                        type: row.type,
                        amount: row.amount,
                        method: row.method,
                        paymentDate: row.paymentDate,
                      },
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search Welfares..."
                  printFullTable // Ensure all columns print regardless of screen size
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

      <MemberDetailsModal
        member={selectedMember}
        open={showMemberDetails}
        onClose={closeMemberDetails}
        churchId={churchId}
      />
    </Box>
  );
}

export default Welfare;
